import { AnnotationType } from '@api/domain/types/annotations';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import type { AnnotationColorConfig } from '@v2/components/imageHandling/components/DrawingCanvas/types/color';
import type { ActiveTool } from '@v2/components/imageHandling/components/DrawingCanvas/types/tool';
import { HandleId } from '@v2/components/imageHandling/components/DrawingCanvas/types/handle';

export type HandleHoriz = 'left' | 'right' | 'none';
export type HandleVert = 'top' | 'bottom' | 'none';

/**
 * Canonical direction of each resize handle. Lookups here replace brittle substring matching on the
 * enum string (`.includes('l')`, etc.) — renaming or adding a handle now forces this map to stay in
 * sync, so resize math can't silently drift.
 */
export const HANDLE_DIRECTION: Record<HandleId, { horiz: HandleHoriz; vert: HandleVert }> = {
  [HandleId.TopLeft]: { horiz: 'left', vert: 'top' },
  [HandleId.TopMid]: { horiz: 'none', vert: 'top' },
  [HandleId.TopRight]: { horiz: 'right', vert: 'top' },
  [HandleId.MidLeft]: { horiz: 'left', vert: 'none' },
  [HandleId.MidRight]: { horiz: 'right', vert: 'none' },
  [HandleId.BottomLeft]: { horiz: 'left', vert: 'bottom' },
  [HandleId.BottomMid]: { horiz: 'none', vert: 'bottom' },
  [HandleId.BottomRight]: { horiz: 'right', vert: 'bottom' },
  [HandleId.Top]: { horiz: 'none', vert: 'top' },
  [HandleId.Right]: { horiz: 'right', vert: 'none' },
  [HandleId.Bottom]: { horiz: 'none', vert: 'bottom' },
  [HandleId.Left]: { horiz: 'left', vert: 'none' },
  [HandleId.Start]: { horiz: 'none', vert: 'none' },
  [HandleId.End]: { horiz: 'none', vert: 'none' },
};

/** Pointer tool — only selects / moves / resizes; never draws. Default tool. */
export const POINTER_TOOL = 'pointer' as const;

/** Eraser is a pointer mode, not an annotation kind — kept as a standalone literal. */
export const ERASER_TOOL = 'eraser' as const;

/** Bottom-toolbar tool order. Importers should use this instead of hard-coding an array. */
export const TOOL_ORDER = [
  POINTER_TOOL,
  AnnotationType.Pen,
  AnnotationType.Highlighter,
  ERASER_TOOL,
  AnnotationType.Arrow,
  AnnotationType.Rectangle,
  AnnotationType.Circle,
  AnnotationType.Text,
] as const;

/** Mapping from toolbar tool to its testID — kept here so tests / helpers can look it up without importing the component. */
export const TOOL_TEST_IDS: Record<ActiveTool, string> = {
  [POINTER_TOOL]: TEST_IDS.drawingCanvas.toolPointer,
  [AnnotationType.Pen]: TEST_IDS.drawingCanvas.toolPen,
  [AnnotationType.Highlighter]: TEST_IDS.drawingCanvas.toolHighlighter,
  [ERASER_TOOL]: TEST_IDS.drawingCanvas.toolEraser,
  [AnnotationType.Arrow]: TEST_IDS.drawingCanvas.toolArrow,
  [AnnotationType.Rectangle]: TEST_IDS.drawingCanvas.toolRectangle,
  [AnnotationType.Circle]: TEST_IDS.drawingCanvas.toolCircle,
  [AnnotationType.Text]: TEST_IDS.drawingCanvas.toolText,
};

/** Human-readable label per annotation type — used by sidebar and similar UI surfaces. */
export const ANNOTATION_TYPE_LABELS: Record<AnnotationType, string> = {
  [AnnotationType.Pen]: 'Pen',
  [AnnotationType.Highlighter]: 'Highlighter',
  [AnnotationType.Arrow]: 'Arrow',
  [AnnotationType.Rectangle]: 'Rectangle',
  [AnnotationType.Circle]: 'Circle',
  [AnnotationType.Text]: 'Text',
};

/** Labels for every entry in the bottom toolbar, including the Eraser pointer mode. */
export const TOOL_LABELS: Record<ActiveTool, string> = {
  ...ANNOTATION_TYPE_LABELS,
  [POINTER_TOOL]: 'Pointer',
  [ERASER_TOOL]: 'Eraser',
};

/** Apple-Markup color palette used by the bottom toolbar. */
export const ANNOTATION_COLORS: readonly AnnotationColorConfig[] = [
  { value: '#FF3B30' },
  { value: '#FF9500' },
  { value: '#FFCC00' },
  { value: '#34C759' },
  { value: '#007AFF' },
  { value: '#AF52DE' },
  { value: '#000000' },
  { value: '#FFFFFF', inactiveBorder: 'Label/Light/Tertiary' },
];

/** Stroke widths in widthOriginal-pixel units (thin, medium, thick). */
export const ANNOTATION_STROKE_WIDTHS: readonly number[] = [2, 4, 7];

export const DEFAULT_STROKE_COLOR = ANNOTATION_COLORS[0].value;
export const DEFAULT_STROKE_WIDTH = ANNOTATION_STROKE_WIDTHS[1];

export const HIGHLIGHTER_OPACITY = 0.4;
export const HIGHLIGHTER_WIDTH_MULTIPLIER = 3;

/** Handle hit radius, expressed as a fraction of the image's smaller dimension. */
export const HANDLE_HIT_RADIUS = 0.035;

/** Handle visual radius in widthOriginal-pixels. */
export const HANDLE_VISUAL_RADIUS = 6;

/** Default font size for a new text annotation in widthOriginal-pixels. */
export const TEXT_DEFAULT_FONT_SIZE = 14;

/** Upper bound on undo/redo stack entries. */
export const UNDO_STACK_LIMIT = 50;

/** Default width (before scale) of the right-side annotation list sidebar. */
export const ANNOTATION_LIST_DEFAULT_WIDTH = 220;

/** Floating drawing-mode bar heights (before scale). */
export const DRAWING_TOP_BAR_HEIGHT = 56;
export const DRAWING_BOTTOM_BAR_HEIGHT = 130;

/** Mobile-only — collapsible "Annotations (n)" strip that replaces the desktop right sidebar. */
export const MOBILE_ANNOTATION_LIST_HEADER_HEIGHT = 36;
export const MOBILE_ANNOTATION_LIST_EXPANDED_HEIGHT = 220;

/** Minimum draggable shape size in widthOriginal-pixels — below this, the shape is discarded on pointer-up. */
export const SHAPE_MIN_SIZE_FRACTION = 0.005;

/** Selection / hover / eraser highlight palette. */
export const SELECTION_STROKE_COLOR = '#007AFF';
export const HIGHLIGHT_SELECTED_COLOR = '#007AFF';
export const HIGHLIGHT_HOVER_COLOR = '#007AFF';
export const HIGHLIGHT_ERASER_COLOR = '#FF3B30';

export const HIGHLIGHT_SELECTED_OPACITY = 0.25;
export const HIGHLIGHT_HOVER_OPACITY = 0.12;
export const HIGHLIGHT_ERASER_OPACITY = 0.45;

/** Stroke outset (widthOriginal-pixels) added around a shape when it is highlighted. */
export const HIGHLIGHT_STROKE_OUTSET = 6;

/** Fill opacity used for the Filled variant of Rectangle / Circle. */
export const SHAPE_FILL_OPACITY = 0.2;

/** Corner radius used by the Rectangle renderer. */
export const RECTANGLE_CORNER_RADIUS = 2;

/** Arrow head geometry. */
export const ARROW_HEAD_BASE_LEN = 16;
export const ARROW_HEAD_STROKE_FACTOR = 3;
export const ARROW_HEAD_HALF_ANGLE = 0.25;
export const ARROW_LINE_RECESS_FACTOR = 0.35;

/** Text approximation multipliers used for hit testing / bounding box. */
export const TEXT_WIDTH_PER_CHAR_FACTOR = 0.6;
export const TEXT_MIN_WIDTH = 30;
export const TEXT_HIGHLIGHT_PADDING = 4;

/** Selection bounding box styling. */
export const SELECTION_BOX_PADDING = 4;
export const SELECTION_BOX_OPACITY = 0.5;
export const SELECTION_BOX_DASH_PATTERN = '4,3';
export const HANDLE_FILL_COLOR = '#FFFFFF';
export const HANDLE_STROKE_WIDTH = 1.5;

/** Eraser strikethrough styling. */
export const ERASER_STRIKE_COLOR = '#FF3B30';
export const ERASER_STRIKE_WIDTH = 2.5;
export const ERASER_STRIKE_OPACITY = 0.7;
export const ERASER_STRIKE_PADDING = 4;

export const IMAGE_ANNOTATION_FEATURE_FLAG = 'image-annotation';